import "./editor-styles.css";

import { Color } from "@tiptap/extension-color";
import ListItem from "@tiptap/extension-list-item";
import TextStyle from "@tiptap/extension-text-style";
import { BubbleMenu, EditorProvider, useCurrentEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import React, { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { Check, Maximize2, Redo, Undo } from "lucide-react";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableRow from "@tiptap/extension-table-row";
import TableHeader from "@tiptap/extension-table-header";
import { ListDepthClasses } from "./list-depth-classes";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

type LinkBubbleMenuProps = {
  onBubbleEnter?: () => void;
  onBubbleLeave?: (e: React.MouseEvent) => void;
};

const LinkBubbleMenu = ({ onBubbleEnter, onBubbleLeave }: LinkBubbleMenuProps) => {
  const { editor } = useCurrentEditor();
  const [isEditingBubble, setIsEditingBubble] = useState(false);
  const [url, setUrl] = useState("");
  const urlInputRef = useRef<HTMLInputElement | null>(null);
  const prevKeyRef = useRef<string>("");

  const currentUrl = editor?.getAttributes("link").href;
  const { from, to } = editor?.state.selection ?? { from: 0, to: 0 };
  // const currentText = editor?.state.doc.textBetween(from, to) ?? '';

  // Keep inputs in sync with current link selection so the user can edit immediately
  // Keep inputs in sync with the current link selection, but only when the selection changes
  // and we're not actively editing in the bubble.
  useEffect(() => {
    if (!editor) return;
    if (!editor.isActive("link")) return;
    if (isEditingBubble) return;
    const key = `${from}-${to}-${currentUrl ?? ""}`;
    if (key !== prevKeyRef.current) {
      prevKeyRef.current = key;
      setUrl(currentUrl || "");
    }
  }, [editor, from, to, currentUrl, isEditingBubble]);

  if (!editor) {
    return null;
  }

  const handleSave = () => {
    if (url === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    }
    setIsEditingBubble(false);
    setTimeout(() => {
      const { to } = editor.state.selection;
      editor.commands.setTextSelection(to);
      editor.commands.unsetMark("link");
    }, 0);
  };

  const handleCancel = () => {
    setIsEditingBubble(false);
    setUrl("");
    setTimeout(() => {
      const { to } = editor.state.selection;
      editor.commands.setTextSelection(to);
      editor.commands.unsetMark("link");
    }, 0);
  };

  return (
    <div
      className="flex items-center gap-2 rounded-md border bg-white p-2 shadow-lg"
      onMouseEnter={onBubbleEnter}
      onMouseLeave={(e) => onBubbleLeave?.(e)}
      onKeyDown={(e) => {
        // Keep typing inside inputs from affecting the editor (e.g., Backspace)
        e.stopPropagation();
      }}
    >
      <div className="flex flex-col items-center gap-2">
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="Enter URL..."
          className="min-w-48 rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none"
          ref={urlInputRef}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.stopPropagation();
              handleSave();
            } else if (e.key === "Escape") {
              handleCancel();
            }
          }}
          onFocus={() => setIsEditingBubble(true)}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </div>
      <div className="flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleSave();
          }}
          className="rounded bg-secondary px-1 py-0.5 text-xs text-white"
        >
          <Check className="w-5" />
        </button>
      </div>
    </div>
  );
};

const MenuBar = () => {
  const { editor } = useCurrentEditor();

  if (!editor) {
    return null;
  }

  const showTableControls = editor.isActive("table");

  return (
    <div className="flex flex-row flex-wrap gap-2 pb-4">
      <div className="flex flex-row flex-wrap gap-2">
        <EditorButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
        >
          Bold
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
        >
          Italic
        </EditorButton>
      </div>

      <div className="flex flex-row flex-wrap gap-2">
        <EditorButton
          onClick={() => editor.chain().focus().setParagraph().run()}
          isActive={editor.isActive("paragraph")}
        >
          Paragraph
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive("heading", { level: 1 })}
        >
          H1
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive("heading", { level: 2 })}
        >
          H2
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive("heading", { level: 3 })}
        >
          H3
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
          isActive={editor.isActive("heading", { level: 4 })}
        >
          H4
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
          isActive={editor.isActive("heading", { level: 5 })}
        >
          H5
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
          isActive={editor.isActive("heading", { level: 6 })}
        >
          H6
        </EditorButton>
        <EditorButton
          onClick={() => {
            if (editor.isActive("link")) {
              editor.chain().focus().unsetLink().run();
            } else {
              editor.chain().focus().setLink({ href: "" }).run();
              setTimeout(() => {
                const { to } = editor.state.selection;
                editor.commands.setTextSelection(to);
              }, 0);
            }
          }}
        >
          Link
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
        >
          Bullet list
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          isActive={editor.isActive("orderedList")}
        >
          Ordered list
        </EditorButton>
        <EditorButton
          onClick={() =>
            editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
          }
          disabled={!editor.can().chain().focus().insertTable({ rows: 3, cols: 3 }).run()}
        >
          Insert Table
        </EditorButton>
      </div>
      <div className="flex flex-row flex-wrap gap-2">
        <EditorButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
        >
          <Undo size={12} className="text-neutral-500" />
        </EditorButton>
        <EditorButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
        >
          <Redo size={12} className="text-neutral-500" />
        </EditorButton>
      </div>

      {/* Table controls */}
      <div className="flex flex-row flex-wrap gap-2">
        {showTableControls && (
          <>
            <EditorButton
              onClick={() => editor.chain().focus().addColumnBefore().run()}
              disabled={!editor.can().chain().focus().addColumnBefore().run()}
            >
              Add Column Before
            </EditorButton>
            <EditorButton
              onClick={() => editor.chain().focus().addColumnAfter().run()}
              disabled={!editor.can().chain().focus().addColumnAfter().run()}
            >
              Add Column After
            </EditorButton>
            <EditorButton
              onClick={() => editor.chain().focus().deleteColumn().run()}
              disabled={!editor.can().chain().focus().deleteColumn().run()}
            >
              Delete Column
            </EditorButton>
            <EditorButton
              onClick={() => editor.chain().focus().addRowBefore().run()}
              disabled={!editor.can().chain().focus().addRowBefore().run()}
            >
              Add Row Before
            </EditorButton>
            <EditorButton
              onClick={() => editor.chain().focus().addRowAfter().run()}
              disabled={!editor.can().chain().focus().addRowAfter().run()}
            >
              Add Row After
            </EditorButton>
            <EditorButton
              onClick={() => editor.chain().focus().deleteRow().run()}
              disabled={!editor.can().chain().focus().deleteRow().run()}
            >
              Delete Row
            </EditorButton>
            <EditorButton
              onClick={() => editor.chain().focus().deleteTable().run()}
              disabled={!editor.can().chain().focus().deleteTable().run()}
            >
              Delete Table
            </EditorButton>
          </>
        )}
      </div>

      {/* <EditorButton
        onClick={() => editor.chain().focus().toggleStrike().run()}
        disabled={!editor.can().chain().focus().toggleStrike().run()}
        isActive={editor.isActive('strike')}
      >
        Strike
      </EditorButton> */}
      {/* <EditorButton
        onClick={() => editor.chain().focus().toggleCode().run()}
        disabled={!editor.can().chain().focus().toggleCode().run()}
        isActive={editor.isActive('code')}
      >
        Code
      </EditorButton> */}

      {/* <EditorButton onClick={() => editor.chain().focus().clearNodes().run()}>
        Clear nodes
      </EditorButton> */}
      {/* <EditorButton
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        isActive={editor.isActive('codeBlock')}
      >
        Code block
      </EditorButton> */}
      {/* <EditorButton
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        isActive={editor.isActive('blockquote')}
      >
        Blockquote
      </EditorButton> */}
      {/* <EditorButton
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        Horizontal rule
      </EditorButton> */}
      {/* <EditorButton onClick={() => editor.chain().focus().setHardBreak().run()}>
        Hard break
      </EditorButton> */}

      {/* <EditorButton
        onClick={() => editor.chain().focus().setColor('#958DF1').run()}
        isActive={editor.isActive('textStyle', { color: '#958DF1' })}
      >
        Purple
      </EditorButton> */}
    </div>
  );
};

const extensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle.configure(),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false, // TODO : Making this as `false` becase marks are not preserved when I try to preserve attrs, awaiting a bit of help
    },
  }),
  Table.configure({
    resizable: true,
  }),
  TableRow,
  TableCell,
  TableHeader,
  Link.configure({
    defaultProtocol: "https",
    openOnClick: true,
    autolink: true,
    linkOnPaste: true,
  }),
  ListDepthClasses,
];

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  expandable?: boolean;
  expandTitle?: string;
}

const ContentSetter = ({
  content,
  isUserInputRef,
}: {
  content: string;
  isUserInputRef: React.MutableRefObject<boolean>;
}) => {
  const { editor } = useCurrentEditor();
  // Normalize content to handle null/undefined/empty string consistently
  const normalizeContent = (val: string | null | undefined): string => {
    return val ?? "";
  };

  // Update editor content when content prop changes externally (not from user input)
  useEffect(() => {
    if (!editor) return;

    // Skip update if this change came from user input
    if (isUserInputRef.current) {
      return;
    }

    // Skip update if editor is focused (user is actively typing)
    if (editor.isFocused) {
      return;
    }

    const normalizedContent = normalizeContent(content);
    const currentContent = editor.getHTML();
    const normalizedCurrentContent = normalizeContent(currentContent);

    // Only update if the content actually changed
    if (normalizedContent !== normalizedCurrentContent) {
      editor.commands.setContent(normalizedContent);
    }
  }, [editor, content, isUserInputRef]);

  return null;
};

const BubbleMenuWrapper = () => {
  const { editor } = useCurrentEditor();
  const prevSelectionRef = useRef<{ from: number; to: number } | null>(null);
  const isHoveringBubbleRef = useRef(false);
  const restoreTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!editor) return;
    const viewDom = editor.view.dom as HTMLElement;

    const handleMouseOver = (e: MouseEvent) => {
      // Cancel any pending restore while re-entering a link
      if (restoreTimerRef.current) {
        window.clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest("a");
      if (!anchor || !viewDom.contains(anchor)) return;

      // Save previous selection only when we're not already inside a link
      if (!editor.isActive("link")) {
        const { from, to } = editor.state.selection;
        prevSelectionRef.current = { from, to };
      }

      const pos = editor.view.posAtCoords({ left: e.clientX, top: e.clientY });
      if (pos?.pos != null) {
        editor.chain().setTextSelection(pos.pos).extendMarkRange("link").run();
      }
    };

    const handleMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const fromAnchor = target?.closest("a");
      const related = e.relatedTarget as HTMLElement | null;
      const toAnchor = related?.closest?.("a");

      // Ignore if moving within the same or another anchor
      if (fromAnchor && toAnchor) return;

      // If moving into the bubble menu (tippy popper with class .bubble-menu), keep selection
      const toBubble =
        related?.closest?.(".bubble-menu") ||
        related?.closest?.(".tippy-box") ||
        related?.closest?.(".tippy-content");
      if (toBubble || isHoveringBubbleRef.current) return;

      // Restore previous selection slightly delayed to allow pointer to reach the bubble
      if (prevSelectionRef.current) {
        if (restoreTimerRef.current) {
          window.clearTimeout(restoreTimerRef.current);
        }
        restoreTimerRef.current = window.setTimeout(() => {
          // If the user entered the bubble in the meantime or is still over a link, skip restore
          if (isHoveringBubbleRef.current || editor.isActive("link")) return;
          editor.commands.setTextSelection(prevSelectionRef.current!);
          prevSelectionRef.current = null;
          restoreTimerRef.current = null;
        }, 150);
      }
    };

    // Use event delegation on the editor DOM
    viewDom.addEventListener("mouseover", handleMouseOver);
    viewDom.addEventListener("mouseout", handleMouseOut);

    return () => {
      viewDom.removeEventListener("mouseover", handleMouseOver);
      viewDom.removeEventListener("mouseout", handleMouseOut);
    };
  }, [editor]);

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor }) => {
        // Only show the bubble menu when a link is active
        return editor.isActive("link");
      }}
      className="bubble-menu"
      tippyOptions={{
        interactive: true,
        placement: "top",
        offset: [0, 8],
        hideOnClick: false,
      }}
    >
      <LinkBubbleMenu
        onBubbleEnter={() => (isHoveringBubbleRef.current = true)}
        onBubbleLeave={(e) => {
          isHoveringBubbleRef.current = false;
          // If we leave the bubble and are not over an anchor inside the editor, restore selection
          const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
          const anchor = el?.closest?.("a");
          const viewDom = editor.view.dom as HTMLElement;
          if (!anchor || !viewDom.contains(anchor)) {
            if (prevSelectionRef.current) {
              editor.commands.setTextSelection(prevSelectionRef.current);
              prevSelectionRef.current = null;
            }
          }
        }}
      />
    </BubbleMenu>
  );
};

const TiptapInstance = ({
  content,
  onChange,
  onExpandClick,
  fullscreen,
}: {
  content: string;
  onChange: (content: string) => void;
  onExpandClick?: () => void;
  fullscreen?: boolean;
}) => {
  // Track if we're currently updating from user input to prevent circular updates
  const isUserInputRef = useRef<boolean>(false);

  const handleUpdate = ({ editor }: { editor: Editor }) => {
    // Mark that we're updating from user input
    isUserInputRef.current = true;
    onChange(editor.getHTML());
    // Reset flag after a short delay to allow the content prop to update
    setTimeout(() => {
      isUserInputRef.current = false;
    }, 0);
  };

  return (
    <div className="relative flex flex-1 flex-col">
      <EditorProvider
        slotBefore={<MenuBar />}
        extensions={extensions}
        content={content}
        onUpdate={handleUpdate}
        immediatelyRender={false}
        editorContainerProps={
          fullscreen
            ? { className: "rich-text-editor-fullscreen flex flex-1 flex-col min-h-0" }
            : undefined
        }
      >
        <BubbleMenuWrapper />
        <ContentSetter content={content} isUserInputRef={isUserInputRef} />
      </EditorProvider>
      {onExpandClick && (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="absolute bottom-3 right-6 bg-white hover:bg-gray-100"
          onClick={onExpandClick}
        >
          <Maximize2 size={14} className="text-gray-500" />
        </Button>
      )}
    </div>
  );
};

export const RichTextEditor = ({
  content,
  onChange,
  expandable = false,
  expandTitle = "Edit Content",
}: RichTextEditorProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      <TiptapInstance
        content={content}
        onChange={onChange}
        onExpandClick={expandable ? () => setIsExpanded(true) : undefined}
      />
      {expandable && (
        <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
          <DialogContent className="flex h-[90vh] max-w-[90vw] flex-col gap-0 p-4">
            <DialogHeader className="pb-2">
              <DialogTitle className="text-sm font-medium">{expandTitle}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-1 flex-col overflow-hidden">
              <TiptapInstance content={content} onChange={onChange} fullscreen />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" onClick={() => setIsExpanded(false)}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

interface EditorButtonProps {
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
  children: React.ReactNode;
}

const EditorButton = ({
  onClick,
  disabled = false,
  isActive = false,
  children,
}: EditorButtonProps) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        isActive ? "bg-neutral-200" : "",
        "whitespace-nowrap rounded-md border border-neutral-200 px-2 py-1 text-xs",
      )}
      type="button"
    >
      {children}
    </button>
  );
};
